import React from 'react';
import { Phone, Mail, Check } from 'lucide-react';
import { company, teamGroups, aboutFaqs } from '../mock';
import { PageFaq } from '../sections/RestSections';

const kylePhoto = teamGroups[0].members[0].photo;

const familyPhotos = [
  { src: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_lowcountry-test/artifacts/4xu453z5_image.png', alt: 'The Hagemes family in front of the Victory Plumbing van' },
  { src: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_lowcountry-test/artifacts/oytaq4sl_Screenshot%202026-08-09%20at%208.10.03%E2%80%AFPM.webp', alt: 'Victory Plumbing family at a local community event' },
  { src: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_lowcountry-test/artifacts/xf2tibrq_Screenshot%202026-08-09%20at%208.09.40%E2%80%AFPM.webp', alt: 'Victory Plumbing at a Savannah community booth' },
];

const values = [
  'Local, family-owned and operated',
  'Customer-centered from the first call to the final walkthrough',
  'Clear communication, so you always understand the work',
  'Transparent, upfront pricing',
  'High-grade materials chosen to last',
  'Every job built with longevity in mind',
  'Added expertise in historic structures and the challenges of coastal living',
];

export default function AboutPage() {
  return (
    <main className="page about-page">
      <section className="page-hero">
        <div className="wrap">
          <p className="eyebrow">About Us</p>
          <h1>Reliable plumbing, built on integrity</h1>
          <p className="lede">
            Victory Plumbing delivers high-quality plumbing for homes and businesses across Savannah and the
            Coastal Empire. From repairs and drain cleaning to water heater installation, leak detection, and
            repiping, our licensed team brings careful, dependable workmanship to every job &mdash; completed
            safely, efficiently, and to the highest industry standards.
          </p>
        </div>
      </section>

      <section className="about-body">
        <div className="wrap">
          <div className="about-intro">
            <div>
              <p className="eyebrow">Our Story</p>
              <h2>Meet Kyle &amp; Kelley Hagemes, founders</h2>
              <p>
                Victory Plumbing was founded by Kyle Hagemes and his partner, Kelley Hagemes. Kyle brings more
                than 25 years in the trade and a genuine passion for the craft, while Kelley shapes the brand and
                customer experience behind the scenes. Together they built the company alongside their family with
                one goal: to give Savannah a different caliber of service, grounded in integrity and high standards.
              </p>
              <p>
                A classic tradesman with a methodical, systems-level understanding of plumbing, Kyle began as a
                teenager apprenticing with F.C. Hires Plumbing on Tybee Island. Since then he has worked in every
                facet of the field, advancing to a licensed <b>Class II (Unrestricted) Master Plumber ({company.licenseNumber})</b>. That
                hands-on foundation shapes how he and his crew approach every project today.
              </p>
              <div className="about-cta">
                <a className="btn btn-gold" href={company.phoneHref}><Phone size={16} /> {company.phoneDisplay}</a>
                <a className="btn btn-outline-dark" href={`mailto:${company.email}`}><Mail size={16} /> Email Us</a>
              </div>
            </div>
            <div className="about-photo">
              <img src={kylePhoto} alt="Kyle Hagemes, founder of Victory Plumbing" loading="lazy" />
            </div>
          </div>

          <div className="family-strip" data-testid="family-strip">
            <div className="sec-head" style={{ marginBottom: '22px' }}>
              <p className="eyebrow" style={{ justifyContent: 'center' }}>Family Owned &amp; Operated</p>
              <h3>A family business, serving our Savannah neighbors</h3>
            </div>
            <div className="family-grid">
              {familyPhotos.map((p) => (
                <div className="family-photo" key={p.src}>
                  <img src={p.src} alt={p.alt} loading="lazy" />
                </div>
              ))}
            </div>
          </div>

          <div className="about-craft">
            <p className="eyebrow" style={{ justifyContent: 'center' }}>Craftsmanship You Can Count On</p>
            <h3>Done right, from minor repairs to major projects</h3>
            <p>
              Whatever the size of the job, our customers can count on it being done right, with expertise and
              stellar craftsmanship. Residential repairs and installations, pipe and sewer line replacements,
              renovations, and tankless water heaters are just a sample of what we resolve &mdash; and we offer
              clear guidance from start to finish. From quick fixes to complete overhauls, Victory Plumbing is
              here to serve you.
            </p>
          </div>
        </div>
      </section>

      <section className="about-values">
        <div className="wrap">
          <div className="sec-head">
            <p className="eyebrow">Why Victory Plumbing?</p>
            <h2>Why homeowners &amp; businesses choose us</h2>
          </div>
          <div className="val-grid">
            {values.map((v) => (
              <div className="val-item" key={v}>
                <span className="ic"><Check size={18} /></span>
                <span>{v}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <PageFaq title="About Victory Plumbing FAQs" subtitle="A little more about who we are and how we work." items={aboutFaqs} testid="about-faqs" />
    </main>
  );
}
